import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { Configuration } from 'src/app/app.constants';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { Employee } from '../../../models/employee';
import { AuthenticationService } from '../../../services/authentication.service';
import { AccountSelectItemViewModel } from '../../../viewModels/accountSelectItemViewModel';
import { AccountSettingsService } from '../../../services/account-settings.service';
import { CachingService } from '../../../services/caching.service';
import { T } from 'src/assets/i18n/translation-keys';

@Component({
  selector: 'app-select-account',
  templateUrl: './select-account.component.html',
    styleUrls: ['./select-account.component.scss'],
    standalone: false,
})
export class SelectAccountComponent implements OnInit {
  @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;
  public isInitialLogin: boolean = false;
  public isFromUnauthorized: boolean = false;
  public isLoading: boolean = true;
  public employee: Employee;
  public accounts: AccountSelectItemViewModel[] = [];
  public localUrl: string;
  public readonly T = T;

  accountSelected = new EventEmitter<AccountSelectItemViewModel>();

  constructor(
    private readonly configuration: Configuration,
    private readonly authenticationService: AuthenticationService,
    private readonly accountSettingsService: AccountSettingsService,
    private readonly cachingService: CachingService,
    public readonly modalRef: BsModalRef,
    private readonly authService: AuthService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) { }
  ngOnInit() {
    this.localUrl = this.configuration.BackEndAPI;
    this.employee = this.authenticationService.getCurrentEmployee();
    let employeeEmail = this.employee?.username;
    this.accounts = [];

    console.log('SelectAccountComponent - employee:', this.employee);
    console.log('SelectAccountComponent - employee email:', employeeEmail);

    if (!employeeEmail) {
      employeeEmail = this.authService.getActiveEmployeeEmail();
      console.log('SelectAccountComponent - email from authService:', employeeEmail);
    }

    if (employeeEmail) {
      this.getApplicableAccounts(employeeEmail);
    } else {
      console.error('SelectAccountComponent - No email found, cannot fetch accounts');
      this.isLoading = false;
      // Accounts will remain empty, template will show "no accounts" message
    }

    this.route.url.subscribe(res =>  {
      console.log(res);
    })
  }

  getApplicableAccounts(employeeEmail: string): void {
    this.isLoading = true;
    console.log('SelectAccountComponent - Fetching accounts for email:', employeeEmail);

    this.accountSettingsService.getApplicableAccounts(employeeEmail).subscribe(
      res => {
        console.log('SelectAccountComponent - Accounts fetched:', res);
        this.accounts = res;
        this.isLoading = false;
      },
      error => {
        console.error('SelectAccountComponent - Error fetching accounts:', error);
        this.accounts = [];
        this.isLoading = false;
      }
    );
  }

  onLoginWithAccount(account: AccountSelectItemViewModel) {
    if (!account.isActive) {
      console.warn('SelectAccountComponent - Account is not active, aborting login');
      return;
    }

    console.log('SelectAccountComponent - Logging in with account:', account);
    console.log('=== CACHING ACCOUNT CONFIGURATION ===');

    // STEP 1: Cache Account ID (used by HTTP interceptor)
    if (account.accountID) {
      console.log('SelectAccountComponent - Caching Account ID:', account.accountID);
      localStorage.setItem('selectedAccountId', account.accountID.toString());
    } else {
      console.warn('SelectAccountComponent - No accountID provided');
    }

    // STEP 2: Cache Account UID (used for routing and account identification)
    if (account.accountGUID) {
      console.log('SelectAccountComponent - Caching Account UID:', account.accountGUID);
      this.cachingService.addAccountUID(account.accountGUID);
    } else {
      console.warn('SelectAccountComponent - No accountGUID provided');
    }

    // STEP 3: Update Backend API URL in Configuration service AND cache
    // CRITICAL: Must call initBackendAPI() to update in-memory cache before reload
    if (account.apiURL) {
      console.log('SelectAccountComponent - Initializing Backend API URL:', account.apiURL);
      this.configuration.initBackendAPI(account.apiURL);
      console.log('SelectAccountComponent - Backend API URL initialized and cached');
    } else {
      console.warn('SelectAccountComponent - No apiURL provided, using environment default');
      this.configuration.initBackendAPI(null);
    }

    console.log('=== ACCOUNT CONFIGURATION CACHED - RELOADING APP ===');

    // STEP 4: Close modal
    this.modalRef.hide();

    // STEP 5: Reload the entire app to reinitialize with new configuration
    // This ensures:
    // - loadAllDataInMemoryFromStorage() loads all cached values
    // - App detects hasAccountStored = true
    // - Backend URL is loaded from cache before any API calls
    // - hasAccessToAccount() will NOT overwrite the cached backend URL (see app.component.ts fix)
    // - No race conditions or timing issues

    // STEP 5: Navigate based on platform
    setTimeout(() => {
      if (!Capacitor.isNativePlatform() && account.accountGUID) {
        // WEB: Navigate to account's frontend URL with accountGUID
        // Use account.url as base (e.g., https://feature2.wetrack.com)
        const baseUrl = account.url?.trim().replace(/\/$/, '') || window.location.origin;
        const newUrl = `${baseUrl}/${account.accountGUID}/mytrack/planning`;

        console.log('SelectAccountComponent - Web platform, navigating to:', newUrl);
        window.location.href = newUrl;
      } else {
        // NATIVE (iOS/Android): Reload the app
        console.log('SelectAccountComponent - Native platform, reloading app...');
        window.location.reload();
      }
    }, 300); // Small delay to ensure modal closes and cache writes complete
  }

  loggedInDays(account: AccountSelectItemViewModel): string {
    const prefix = 'Last logged in';
    const neverLoggedIn = 'Never Logged in';

    if (account.lastLoggedIn > 1) {
      return `${prefix} ${account.lastLoggedIn} days ago`;
    } else if (account.lastLoggedIn > -1 && account.lastLoggedIn <= 1) {
      if (!account.lastLoggedIn) {
        return `${prefix} today`;
      } else {
        return `${prefix} yesterday`;
      }
    } else if (account.lastLoggedIn < 0) {
      return neverLoggedIn;
    }
  }

  onLogout() {
    this.authService.logout();
  }

  /**
   * Extracts the hostname from a URL (domain only, without protocol or path)
   * @param url - The full URL string
   * @returns The hostname (domain) only
   */
  private extractHostname(url: string): string {
    if (!url) {
      return '';
    }
    try {
      // Use URL API to properly extract just the hostname
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (e) {
      // Fallback: remove protocol and extract hostname before first slash
      const withoutProtocol = url.replace(/^https?:\/\//, '');
      const hostname = withoutProtocol.split('/')[0];
      return hostname;
    }
  }
}
